<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-x-full"
    enter-to-class="opacity-100 translate-x-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-x-0"
    leave-to-class="opacity-0 translate-x-full"
  >
    <div
      v-if="visible"
      class="max-w-xs bg-white border border-gray-200 rounded-xl shadow-lg mb-3 relative"
      role="alert"
      tabindex="-1"
      :aria-labelledby="`toast-${toast.id}-label`"
    >
      <div class="flex p-4">
        <div class="shrink-0">
          <component :is="iconComponent" :class="iconClasses" />
        </div>
        <div class="ms-3 flex-1">
          <p :id="`toast-${toast.id}-label`" class="text-sm text-gray-700">
            {{ toast.message }}
          </p>
        </div>
        <div v-if="showCloseButton" class="ms-2">
          <button
            @click="close"
            type="button"
            class="inline-flex shrink-0 justify-center items-center size-5 rounded-lg text-gray-800 opacity-50 hover:opacity-100 focus:outline-none focus:opacity-100"
            aria-label="Close"
          >
            <span class="sr-only">{{ $t('common.srOnly.close') }}</span>
            <X class="shrink-0 size-4" />
          </button>
        </div>
      </div>
      <!-- Progress bar for auto-close -->
      <div
        v-if="showProgress && toast.timeout && toast.timeout > 0"
        class="absolute bottom-[0.34px] w-[98%] left-1/2 -translate-x-1/2 h-1 bg-gray-100 rounded-b-xl overflow-hidden"
      >
        <div
          class="h-full transition-all ease-linear"
          :class="progressBarClasses"
          :style="{ width: `${progress}%` }"
        ></div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-vue-next'

// Local interface for this component only
interface Toast {
  id: number
  message: string
  type: 'success' | 'error' | 'warning' | 'info'
  timeout?: number
}

interface Props {
  toast: Toast
  showCloseButton?: boolean
  showProgress?: boolean
}

interface Emits {
  (e: 'close', id: number): void
}

const props = withDefaults(defineProps<Props>(), {
  showCloseButton: true,
  showProgress: true
})

const emit = defineEmits<Emits>()

// Component state
const visible = ref(true)
const progress = ref(100)
let progressInterval: number | null = null
let autoCloseTimeout: number | null = null

// Icon mapping
const iconComponents = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info
}

// Computed properties
const iconComponent = computed(() => iconComponents[props.toast.type])

const iconClasses = computed(() => {
  const baseClasses = 'shrink-0 size-4 mt-0.5'
  const colorClasses = {
    success: 'text-teal-500',
    error: 'text-red-500',
    warning: 'text-yellow-500',
    info: 'text-blue-500'
  }

  return `${baseClasses} ${colorClasses[props.toast.type]}`
})

const progressBarClasses = computed(() => {
  const colorClasses = {
    success: 'bg-teal-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  }

  return colorClasses[props.toast.type]
})

// Methods
const close = () => {
  visible.value = false

  // Wait for transition to complete before emitting close event
  setTimeout(() => {
    emit('close', props.toast.id)
  }, 200)
}

const startAutoClose = () => {
  if (!props.toast.timeout || props.toast.timeout <= 0) return

  const startTime = Date.now()
  const duration = props.toast.timeout

  
  if (props.showProgress) {
    progressInterval = window.setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, duration - elapsed)
      progress.value = (remaining / duration) * 100

      if (remaining <= 0) {
        clearInterval(progressInterval!)
        progressInterval = null
      }
    }, 50)
  }

  // Auto close
  autoCloseTimeout = window.setTimeout(() => {
    close()
  }, duration)
}

const stopAutoClose = () => {
  if (progressInterval) {
    clearInterval(progressInterval)
    progressInterval = null
  }

  if (autoCloseTimeout) {
    clearTimeout(autoCloseTimeout)
    autoCloseTimeout = null
  }
}

// Lifecycle
onMounted(() => {
  startAutoClose()
})

onUnmounted(() => {
  stopAutoClose()
})
</script>
